import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import Vuetable, { VuetablePagination, VuetablePaginationInfo } from 'vuetable-2';
import { formatDate, formatDateTime, WindowType } from '../../util';
import vuetableCss from '../activity/style';
import ActivitylogmodalComponent from '../activitylogmodal/activitylogmodal.controller';
import { EnumStatus } from '../activitymodal/activitymodal.model';
import { ActivityControllerUIClient, ActivityLog, ActivityLogDistinctValues, ActivityPriority, Cell, CellClient, Duty } from '../backend/backendapi';
import { Operator, SearchEvent, Segment, Suggestion } from "../search/search.model";
import { initClient } from "../../api";
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import Search from "../search/search.controller";
import PrintModal from './PrintModal';

declare var window: WindowType;

@Component({
    name: 'ActivitylogComponent',
    components: {
        PrintModal,
        Vuetable,
        VuetablePagination,
        VuetablePaginationInfo,
        DateRangePicker,
        ActivityLogModal: require('../activitylogmodal/activitylogmodal.vue.html').default,
        Search: require('../search/search.vue.html').default,
    }
})
export default class ActivitylogComponent extends Vue {
    //data
    private readonly cells: Cell[] = [];
    private availablePriorities: ActivityPriority[] = [];

    //api
    private readonly activityAPI = initClient(ActivityControllerUIClient);
    private readonly cellApi = initClient(CellClient);

    //runtime
    private duty: Duty = new Duty();
    private activity: ActivityLog = new ActivityLog();
    private sortOrder: any[] = [];
    private moreparams: object = {};
    private searchBoxQuery = '';
    private dateRangeQuery = '';
    private dateRange: { startDate: Date | null, endDate: Date | null } = { startDate: null, endDate: null };
    private dateRangePickerLocale: any;
    private filterTimeout: number | null = null;
    private activityLogDistinctValues: ActivityLogDistinctValues | null = null;
    private doneOnly: boolean = true;
    private statusFilter: 'all' | 'open' | 'done' = 'all';

    //static
    private fields: any[] = [];
    private css: object = vuetableCss.table;

    formatDate = formatDate;

    created(): void {
        var d = new Date();
        d.setMonth(d.getMonth() - 1);
        d.setHours(0, 0, 0, 0);
        this.dateRange.startDate = d;
        this.dateRange.endDate = new Date();
        this.updateDateRange();
        this.updateMoreParams();
        this.fields = [
            {
                name: 'status',
                title: '<span class="fas fa-user-check"></span> ',
                formatter: (value) => this.getStatus(value)
            },
            {
                name: 'wasOverDue',
                title: '<span class="fas fa-check" title="' + this.$t('txtDueDate') + '"></span> ',
                formatter: (value) => this.wasOverDue(value)
            },
            {
                name: 'currentDate',
                title: this.getDateStr(),
                sortField: 'currentDate',
                formatter: formatDateTime
            },
            {
                name: 'title',
                sortField: 'title',
                title: this.getTitleStr(),
                formatter: this.escapeTags,
            },
            {
                name: 'dutyTitle',
                title: this.getDutyStr(),
                formatter: this.escapeTags,
            },
            {
                settingsName: 'Cell',
                name: 'cellName',
                title: this.getCellStr(),
                sortField: 'cellname',
                formatter: this.escapeTags,
            },
            {
              settingsName: 'Prio',
              name: 'priority.name',
              title: this.getPrionameStr(),
              formatter: this.escapeTags,
            },
            {
              settingsName: 'ActivityId',
              name: 'id',
              title: this.getIdStr(),
            },
            {
                name: 'username',
                title: this.getUsernameStr(),
                formatter: this.escapeTags,
                sortField: 'username',
            },
            'actions'];
        this.sortOrder = [
            { field: 'dutyTitle', direction: 'asc' },
            { field: 'currentDate', direction: 'asc' }
        ];

        this.dateRangePickerLocale = this.$i18n.locale === 'de'
            ? {
                direction: 'ltr',
                format: this.$t('dateFormatString'),
                separator: ' - ',
                applyLabel: 'Übernehmen',
                cancelLabel: 'Abbrechen',
                weekLabel: 'W',
                customRangeLabel: 'Custom Range',
                daysOfWeek: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
                monthNames: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
                firstDay: 1
            } : {
                direction: 'ltr',
                format: this.$t('dateFormatString'),
                separator: ' - ',
                applyLabel: 'Apply',
                cancelLabel: 'Cancel',
                weekLabel: 'W',
                customRangeLabel: 'Custom Range',
                daysOfWeek: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
                monthNames: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                firstDay: 1
            };

        this.handleSearchTriggerByUrl();
    }

    async mounted(): Promise<void> {
        this.handleSearchTriggerByUrl();
        await this.reloadCells();
        await this.reloadActivityLogDistinctValues();
        await this.reloadPriorities();
    }

    private async reloadCells(): Promise<void> {
        const cells = await this.cellApi.getCellsData();
        this.cells.splice(0);
        this.cells.push(...cells);
    }

    private async reloadActivityLogDistinctValues(): Promise<void> {
        this.activityLogDistinctValues = await this.activityAPI.getLogDistinctValues();
    }

    private async reloadPriorities(): Promise<void> {
    const prios = (await this.activityAPI.getAvailablePriorities()).result!;
    this.availablePriorities.splice(0);
    this.availablePriorities.push(...prios);
  }

    private handleSearchTriggerByUrl(): void {
        let url = new URL(window.location.href);
        if (url.searchParams.has('filter')) {
            let filter = url.searchParams.get('filter')!;

            let segments = filter.split("\n").map(f => {
                let kv = f.split('=', 2);
                let sug = this.suggestions.find(s => s.key.toLocaleLowerCase() === kv[0].toLocaleLowerCase());
                if (!sug) {
                    return null;
                }
                let op = sug.operators.find(e => e.display === '=');
                if (!op) {
                    return null;
                }
                let seg = new Segment<any, any, any>(sug);
                seg.operator = op;
                seg.value = kv[1].startsWith('"') && kv[1].endsWith('"') && kv[1].length > 1 
                    ? kv[1].substring(1, kv[1].length - 1) 
                    : kv[1];
                return seg;
            }).filter(e => e).map(e => e!);

            //set search query
            this.convertSearchEventToSearchQuery(new SearchEvent(segments, ''));
            this.updateMoreParams();

            //set search box state if present
            const searchbox = this.$refs.search as Search;
            if (searchbox) {
                searchbox.setSearch(segments, '');
            }
        }
    }

    private refresh(): void {
        let vuetable = this.$refs.vuetable as any;
        vuetable.reload();
    }

    private transformToOpenApi(data: { data: ActivityLog[] }) {
        data.data = data.data.map(e => {
            let a = new ActivityLog();
            a.init(e);
            return a;
        });
        return data;
    }

    private convertSearchEventToSearchQuery(event: SearchEvent<any, any, ActivityLog>): void {
        let predicates = event.segments
            .filter(s => s.operator && s.value.trim().length)
            .map(s => s.suggestion.key + s.operator?.display + s.value);
        if (event.search.trim().length) {
            predicates.push('search=' + event.search.trim());
        }

        this.searchBoxQuery = predicates.join("\n");
    }

    private updateMoreParams(): void {
      let filter = this.searchBoxQuery + "\n" + this.dateRangeQuery;

      // Handle the 3-way status filter
      if (this.statusFilter === 'done') {
          filter += '\nstatus=Done';
      } else if (this.statusFilter === 'open') {
          filter += '\nstatus=Open';
      }

      if (filter.trim()) {
          this.moreparams = { 'filter': filter.trim() };
      } else {
          this.moreparams = {};
      }
    }

    //events
    private onActionClicked(data: ActivityLog) {
        this.activity = data;
        this.duty = new Duty();
        this.duty.title = data.dutyTitle!;
        (this.$refs.activityLogInfoModal as ActivitylogmodalComponent).openModal();
    }

    private onPaginationData(paginationData): void {
        (this.$refs.pagination as any).setPaginationData(paginationData);
        (this.$refs.paginationinfo as any).setPaginationData(paginationData);
    }

    private onChangePage(page): void {
        var vuetable = this.$refs.vuetable as any;
        vuetable.changePage(page);
    }

    private onSearch(event: SearchEvent<any, any, ActivityLog>): void {
        this.convertSearchEventToSearchQuery(event)
        this.onFilterChanged();
    }

    private onDateRangeUpdate(ev: { startDate: Date | null, endDate: Date | null }): void {
        this.dateRangeQuery = '';
        if (ev !== undefined)
          this.updateDateRange();
        this.onFilterChanged();
    }

    private updateDateRange() {
        if (this.dateRange.startDate) {
            this.dateRangeQuery += "dateFrom=" + this.dateRange.startDate.toISOString() + "\n";
        }
        if (this.dateRange.endDate) {
            this.dateRangeQuery += "dateTo=" + this.dateRange.endDate?.toISOString();
        }
    }

    private onFilterChanged(): void {
        console.log('Current Filter:', this.statusFilter); // Remove .value
        var moreparamsbefore = { ...this.moreparams };
        this.updateMoreParams();

        //check if filter changed
        if (JSON.stringify(moreparamsbefore) === JSON.stringify(this.moreparams)) {
            return;
        }

        let table = this.$refs.vuetable as Vuetable;
        table.gotoPage(table.initialPage);

        if (this.filterTimeout) {
            window.clearTimeout(this.filterTimeout);
        }
        this.filterTimeout = window.setTimeout(this.refresh, 500);
    }

    //getters
    private getDateStr(): string {
        var str = this.$i18n.t('txtActivityDate').toString();
        return '<i class="fas fa-history"></i> ' + str;
    }

    private getTitleStr(): string {
        var str = this.$i18n.t('txtActivityTitle').toString();
        return '<i class="fas fa-check-circle"></i> ' + str;
    }

    private getDutyStr(): string {
        var str = this.$i18n.t('txtDutyName').toString();
        return '<i class="fas fa-tasks"></i> ' + str;
    }

    private getCellStr(): string {
        var str = this.$i18n.t('activityCellName').toString();
        return '<i class="fas fa-hdd"></i> ' + str;
    }

    private getPrionameStr(): string {
      var str = this.$i18n.t('activityPrio').toString();
      return '<i class="fas fa-exclamation"></i> ' + str;
    }
    private getIdStr(): string {
      var str = this.$i18n.t('acsActivityId').toString();
      return '<i class="fas fa-id-badge"></i> ' + str;
    }

    private getUsernameStr(): string {
        var str = this.$i18n.t('txtUsername').toString();
        return '<i class="fas fa-user"></i> ' + str;
    }

    private get mycss() {
        return vuetableCss;
    }

    private get suggestions(): Suggestion<any, any, ActivityLog>[] {
        return [
            new Suggestion('cellIdent', this.$t('cellIdent').toString(), [
                new Operator('eq', '=', (ex: string, ac: string) => ex.toLocaleLowerCase().includes(ac.toLocaleLowerCase())),
            ], this.distinctCellIdents, (c: ActivityLog) => c.cellIdent ?? ''),
            new Suggestion('cellName', this.$t('activityCellName').toString(), [
                new Operator('eq', '=', (ex: string, ac: string) => ex.toLocaleLowerCase().includes(ac.toLocaleLowerCase())),
            ], ['NA', ...this.cells.map(e => e.name!).sort((l, r) => l.toLocaleLowerCase().localeCompare(r.toLocaleLowerCase()))], (c: ActivityLog) => c.cellName ?? ''),
            new Suggestion('description', this.$t('activityDescription').toString(), [
                new Operator('eq', '=', (ex: string, ac: string) => ex.toLocaleLowerCase().includes(ac.toLocaleLowerCase())),
            ], this.distinctDescriptions, (c: ActivityLog) => c.description ?? ''),
            // new Suggestion('duty.ident', this.$t('dutyIdent').toString(), [
            //     new Operator('eq', '=', (ex: string, ac: string) => ex.toLocaleLowerCase().includes(ac.toLocaleLowerCase())),
            // ], this.distinctIdents, (c: ActivityLog) => c.dutyIdent ?? ''),
            new Suggestion('dutyTitle', this.$t('activityDutyTitle').toString(), [
                new Operator('eq', '=', (ex: string, ac: string) => ex.toLocaleLowerCase().includes(ac.toLocaleLowerCase())),
            ], this.distinctDutyTitles, (c: ActivityLog) => c.dutyTitle ?? ''),
        ];
    }

    private get distinctCellIdents(): string [] {
        return this.activityLogDistinctValues?.cellIdents.sort() ?? [];
    }
    
    private get distinctDescriptions(): string [] {
        return this.activityLogDistinctValues?.descriptions.sort() ?? [];
    }
    
    private get distinctDutyIdents(): string [] {
        return this.activityLogDistinctValues?.dutyIdents.sort() ?? [];
    }

    private get distinctDutyTitles(): string [] {
        return this.activityLogDistinctValues?.dutyTitles.sort() ?? [];
    }

    //mappers
    private getStatus(value): string {
        switch (value) {
            case EnumStatus.Triggered: {
                var str = this.$i18n.t('txtTriggered').toString();
                return '<i class="fas fa-check" ></i> ' + str;
            }
            case EnumStatus.Changed: {
                var str = this.$i18n.t('txtChanged').toString();
                return '<i class="fas fa-check" ></i> ' + str;
            }
            case EnumStatus.Postponed: {
                var str = this.$i18n.t('txtPostponed').toString();
                return '<i class="fas fa-check" ></i> ' + str;
            }
            case EnumStatus.SignedOff: {
                var str = this.$i18n.t('txtSignedOff').toString();
                return '<i class="fas fa-check" ></i> ' + str;
            }
            case EnumStatus.Workflow: {
                var str = this.$i18n.t('txtWorkflow').toString();
                return '<i class="fas fa-check" ></i> ' + str;
            }
            case EnumStatus.Document: {
                var str = this.$i18n.t('txtDocument').toString();
                return '<i class="fas fa-check" ></i> ' + str;
            }
            case EnumStatus.Done: {
                var str = this.$i18n.t('txtDone').toString();
                return '<i class="fas fa-check" ></i> ' + str;
            }
            default: {
                return 'ND';
            }
        }
    }

    private wasOverDue(value: boolean): string {
        if (value) {
            return '<i class="fas fa-times" style="color: var(--danger)" title="' + this.$t('activityOverDue') + '"></i> ';
        } else {
            return '<i class="fas fa-check" style="color: var(--success)" title="' + this.$t('activityOnTime') + '"></i>';
        }
    }

    private escapeTags(value: string | null | undefined): string | null | undefined {
        let before = value;
        do {
            before = value;
            value = value?.replace('<', '&lt;').replace('>', '&gt;');
        } while (before !== value);
        return value;
    }
}
